'use client';

import React, { useEffect, useState, useContext, useMemo, useCallback, useRef, MouseEvent, startTransition } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
// PERFORMANCE: Lazy load lucide-react icons to reduce initial bundle size (~163KB)
// Icons load after initial render to reduce main-thread work
type LucideIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;
let lucideIconsModule: typeof import('lucide-react') | null = null;
const loadLucideIcons = async () => {
  if (!lucideIconsModule) {
    lucideIconsModule = await import('lucide-react');
  }
  return lucideIconsModule;
};
// Placeholder icons for initial render (critical icons only)
const MenuIconPlaceholder = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
const XIconPlaceholder = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
import TikTokIcon from '@/components/icons/TikTokIcon';
import { MenuSection, MenuItem } from '@/types';
import { Button } from '@/components/ui/button';
import { getBrandColor } from '@/config/brand-colors';
import { useHeader } from '@/contexts/HeaderContext';
import { AdminSidebarContext } from '@/contexts/AdminSidebarContext';
import { AuthContext } from '@/contexts/AuthContext';
import { usePreview } from '@/contexts/PreviewContext';
import { useMedia } from '@/contexts/MediaContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { mediaCache } from '@/lib/media-cache';
import { getPagePriority } from '@/lib/page-priority';
import { preloadPageMedia } from '@/lib/preload-page-media';
import FormRenderer from '@/components/shared/FormRenderer';
import { generateImageSrcSet } from '@/lib/url-normalization';
import { PublicFormData } from '@/lib/public-site-data';

interface HeaderProps {
  menuSection: MenuSection;
  menuItems?: MenuItem[];
  footerMenuSection?: MenuSection | null;
  subscriptionForm?: PublicFormData | null;
}

// Helper function to convert internal URLs to main domain when on any subdomain
const getMenuUrl = (url: string, isExternal?: boolean): string => {
  // Don't modify external links
  if (isExternal) {
    return url;
  }

  // Check if we're on any subdomain (not the main domain)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Check if hostname contains a subdomain (e.g., jobs.e-voque.com, www.e-voque.com, etc.)
    // Main domain would be exactly "e-voque.com" or "www.e-voque.com" (if www is considered main)
    const isSubdomain = hostname !== 'e-voque.com' && hostname.endsWith('.e-voque.com');

    if (isSubdomain) {
      // For internal links, convert to main domain
      // Only convert if it's a relative URL (starts with /)
      if (url.startsWith('/')) {
        return `https://e-voque.com${url}`;
      }
    }
  }

  return url;
};


export default function Header({
  menuSection,
  menuItems = [],
  footerMenuSection = null,
  subscriptionForm = null,
}: HeaderProps) {
  // Mobile sidebar starts collapsed - can be expanded via menu icon in header
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOnFirstThreeSections, setIsOnFirstThreeSections] = useState(true);
  const [currentSection, setCurrentSection] = useState<'hero' | 'about' | 'third' | 'after'>('hero');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [logoLoading, setLogoLoading] = useState<'eager' | 'lazy'>('eager');
  const [showLogo, setShowLogo] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [clientPathname, setClientPathname] = useState<string | null>(null);
  // PERFORMANCE: Lazy load lucide-react icons after initial render to reduce main-thread work
  const [iconsLoaded, setIconsLoaded] = useState(false);
  const [Icons, setIcons] = useState<{
    Menu: LucideIcon;
    X: LucideIcon;
    LogOut: LucideIcon;
    LayoutDashboard: LucideIcon;
    Home: LucideIcon;
    Instagram: LucideIcon;
    Music: LucideIcon;
    ExternalLink: LucideIcon;
  } | null>(null);
  const { setIsHeaderVisible } = useHeader();
  // Safely get auth context - may be undefined if not within AuthProvider (e.g., during SSR/prerendering)
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const logout = authContext?.logout ?? (async () => {
    // No-op if AuthProvider is not available
  });
  const router = useRouter();
  const preview = usePreview();

  // Check if we're in an admin route and get sidebar state
  const pathnameRaw = usePathname();
  const pathname = pathnameRaw || '/'; // Default to '/' if pathname is null during hydration
  const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/admin/dashboard/pages');
  const isHomePage = pathname === '/';
  const adminSidebarContext = useContext(AdminSidebarContext);
  const isAdminSidebarCollapsed = adminSidebarContext?.isCollapsed ?? false;

  // Media prefetching
  const { prefetchPageMedia, setCurrentPageSlug } = useMedia();
  const prefetchedUrls = useRef(new Set<string>());

  // Set current page slug for navigation prefetching
  useEffect(() => {
    const currentSlug = pathname === '/' ? '' : pathname?.replace(/^\//, '').split('?')[0] || '';
    setCurrentPageSlug(currentSlug);
  }, [pathname, setCurrentPageSlug]);

  // Prefetch media and Next.js route on link hover (with debounce to avoid excessive calls)
  const handleLinkHover = useCallback((url: string) => {
    if (prefetchedUrls.current.has(url)) return;

    const targetSlug = url === '/' ? '' : url.replace(/^\//, '').split('?')[0];
    prefetchedUrls.current.add(url);

    // Prefetch Next.js route (page HTML/JS)
    // This ensures the page is ready when user clicks
    try {
      router.prefetch(url);
    } catch (error) {
      // Silently fail - prefetch is optional
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Header] Failed to prefetch route:', error);
      }
    }

    // Prefetch media for target page
    prefetchPageMedia(targetSlug);
  }, [prefetchPageMedia, router]);

  const headerZIndices = useMemo(
    () => ({
      base: 12000,
      overlay: 12001,
      drawer: 12002,
      mobileLogo: 12003,
    }),
    []
  );

  // Always keep header visible
  useEffect(() => {
    setIsHeaderVisible(true);
  }, [setIsHeaderVisible]);

  // In preview mode inside the dashboard, derive the effective page path from the editor slug
  const effectivePathname = useMemo(() => {
    // Use pathname directly - clientPathname causes timing issues with hydration
    const pathToUse = pathname || '/';
    let result: string;
    if (preview?.isPreviewMode && pathToUse.startsWith('/admin/dashboard/pages')) {
      const slugPart = pathToUse.replace('/admin/dashboard/pages', '').replace(/^\/+/, '');
      result = slugPart === '' ? '/' : `/${slugPart}`;
    } else {
      result = pathToUse;
    }
    return result;
  }, [pathname, preview?.isPreviewMode]);

  // Track if component is mounted (client-side only) to fix SSR/hydration issues
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // PERFORMANCE: Lazy load lucide-react icons after initial render to reduce main-thread work
  // Icons load after first paint using requestIdleCallback to avoid blocking render
  useEffect(() => {
    if (iconsLoaded) return;

    const loadIcons = async () => {
      // Use requestIdleCallback to load icons when browser is idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(async () => {
          const module = await loadLucideIcons();
          setIcons({
            Menu: module.Menu,
            X: module.X,
            LogOut: module.LogOut,
            LayoutDashboard: module.LayoutDashboard,
            Home: module.Home,
            Instagram: module.Instagram,
            Music: module.Music,
            ExternalLink: module.ExternalLink,
          });
          setIconsLoaded(true);
        }, { timeout: 2000 }); // Max wait 2 seconds
      } else {
        // Fallback: load after short delay
        setTimeout(async () => {
          const module = await loadLucideIcons();
          setIcons({
            Menu: module.Menu,
            X: module.X,
            LogOut: module.LogOut,
            LayoutDashboard: module.LayoutDashboard,
            Home: module.Home,
            Instagram: module.Instagram,
            Music: module.Music,
            ExternalLink: module.ExternalLink,
          });
          setIconsLoaded(true);
        }, 100);
      }
    };

    loadIcons();
  }, [iconsLoaded]);

  // Track initial load vs navigation
  useEffect(() => {
    // Mark as not initial load after first render
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
      setLogoLoading('lazy');
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Reset to initial load when pathname changes (new page navigation)
  useEffect(() => {
    // On navigation, use lazy loading for logo
    if (!isInitialLoad) {
      setLogoLoading('lazy');
    }
  }, [pathname, isInitialLoad]);

  // Track if user is on first three sections of home page and which specific section
  useEffect(() => {
    if (!isHomePage) {
      setIsOnFirstThreeSections(false);
      setCurrentSection('after');
      return;
    }

    // First three sections typically have these IDs: hero, about, beyond-standards (third)
    const firstThreeSectionIds = ['hero', 'about', 'beyond-standards'];
    const visibleSections = new Set<string>();

    const observers: IntersectionObserver[] = [];

    const setupObserver = (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Section is visible if at least 30% is in viewport
            if (entry.intersectionRatio >= 0.3) {
              visibleSections.add(sectionId);
            } else {
              visibleSections.delete(sectionId);
            }

            // Check if any of the first three sections are visible
            const isOnFirstThree = firstThreeSectionIds.some(id => visibleSections.has(id));
            setIsOnFirstThreeSections(isOnFirstThree);
          });
        },
        {
          threshold: [0, 0.3, 0.5, 1],
          rootMargin: '-10% 0px -10% 0px'
        }
      );

      observer.observe(section);
      observers.push(observer);
    };

    // Set up observers for first three sections
    firstThreeSectionIds.forEach(setupObserver);

    // Function to determine which section is most visible
    // Use requestAnimationFrame to batch all layout reads and avoid forced reflows
    const determineCurrentSection = () => {
      requestAnimationFrame(() => {
        const heroSection = document.getElementById('hero');
        const aboutSection = document.getElementById('about');
        const beyondSection = document.getElementById('beyond-standards');
        const viewportHeight = window.innerHeight;
        const viewportCenter = viewportHeight / 2;

        // Batch all getBoundingClientRect calls together to avoid multiple reflows
        const sections = [
          { id: 'hero' as const, element: heroSection },
          { id: 'about' as const, element: aboutSection },
          { id: 'third' as const, element: beyondSection }
        ].filter(s => s.element);

        // Read all layout properties in one batch
        const rects = sections.map(({ element }) => element ? element.getBoundingClientRect() : null);
        const beyondRect = beyondSection ? beyondSection.getBoundingClientRect() : null;

        // Check if we're past the third section
        if (beyondRect && beyondRect.bottom < viewportCenter) {
          setCurrentSection('after');
          return;
        }

        // Determine which section's center is closest to viewport center
        let closestSection: 'hero' | 'about' | 'third' | 'after' = 'hero';
        let minDistance = Infinity;

        sections.forEach(({ id }, index) => {
          const rect = rects[index];
          if (!rect) return;

          const sectionCenter = rect.top + rect.height / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);

          if (distance < minDistance && rect.top < viewportHeight && rect.bottom > 0) {
            minDistance = distance;
            if (id === 'hero') {
              closestSection = 'hero';
            } else if (id === 'about') {
              closestSection = 'about';
            } else if (id === 'third') {
              closestSection = 'third';
            }
          }
        });

        setCurrentSection(closestSection);
      });
    };

    // Check initial state
    // Use requestAnimationFrame to batch layout reads
    const checkInitialState = () => {
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;
        const sections = firstThreeSectionIds
          .map(id => document.getElementById(id))
          .filter((section): section is HTMLElement => section !== null);

        // Batch all getBoundingClientRect calls
        const rects = sections.map(section => section.getBoundingClientRect());

        const isOnFirstThree = rects.some(rect => {
          // Check if section is in the middle 80% of viewport
          return rect.top < viewportHeight * 0.9 && rect.bottom > viewportHeight * 0.1;
        });
        setIsOnFirstThreeSections(isOnFirstThree);
        determineCurrentSection();
      });
    };

    // Check after a short delay to ensure DOM is ready
    setTimeout(checkInitialState, 100);

    // Debounced scroll handler to reduce layout thrash
    let scrollTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      // Cancel previous timeout
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout as any);
      }
      // Use requestAnimationFrame to batch with other animations
      scrollTimeout = requestAnimationFrame(() => {
        determineCurrentSection();
      }) as any;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observers.forEach(observer => observer.disconnect());
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout as any);
      }
    };
  }, [isHomePage]);

  // Track scroll direction to hide/show logo
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;

      // Show logo when at top or scrolling up, hide when scrolling down
      if (currentScrollY <= 0) {
        setShowLogo(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down
        setShowLogo(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setShowLogo(true);
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(handleScrollDirection);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Extract logo URL early (before early return) to use in useEffect
  const logo = menuSection?.content?.logo as string | undefined;
  const hasLogo = logo && logo.trim() !== '';
  const logoUrl = hasLogo && logo ? logo.trim() : undefined;

  // Preload logo using mediaCache with priority based on current page
  useEffect(() => {
    if (!logoUrl) return;

    // Determine priority based on current page
    const currentSlug = pathname === '/' ? '' : pathname.replace(/^\//, '').split('?')[0];
    const pagePriority = getPagePriority(currentSlug);
    const logoPriority: 'high' | 'low' = pagePriority >= 9 ? 'high' : 'low';

    // Preload logo using mediaCache (it handles caching internally)
    mediaCache.preloadBatch([logoUrl], logoPriority).catch(() => {
      // Silently handle errors
    });
  }, [logoUrl, pathname]);

  if (!menuSection || !menuSection.isActive) {
    return null;
  }

  // Use values from JSON data
  const logoAlt = (menuSection.content?.logoAlt as string) || 'Logo';
  // Use links from JSON if available, otherwise fall back to menuItems
  const contentLinks = menuSection.content?.links as Array<{ label: string; url: string; isExternal?: boolean }> | undefined;

  // Define the desired menu order (main navigation items only)
  const menuOrder: Record<string, number> = {
    'Home': 0,
    'About Us': 1,
    'Services': 2,
    'Job Benefits': 3,
    'Contact Us': 4,
  };

  // Function to get sort order for menu items
  const getMenuOrder = (label: string): number => {
    return menuOrder[label] ?? 999; // Items not in the order list go to the end
  };

  const rawMenuItems = contentLinks && contentLinks.length > 0
    ? contentLinks.map((link, index) => ({
      id: `json-link-${index}`,
      label: link.label,
      url: link.url,
      isExternal: link.isExternal || false,
      order: index,
      isActive: true
    }))
    : (menuItems || []).filter(item => item.isActive).sort((a, b) => a.order - b.order);

  // Sort menu items by the predefined order, ensuring main nav items come first
  const activeMenuItems = rawMenuItems.sort((a, b) => {
    const orderA = getMenuOrder(a.label);
    const orderB = getMenuOrder(b.label);
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    // If both are not in the predefined order, maintain their original order
    return a.order - b.order;
  });

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Navigation handler - optimized for performance with non-blocking preload
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, url: string, isExternal?: boolean, closeMenuFirst?: boolean) => {
    console.log('[Header] handleNavigation called:', { url, isExternal, closeMenuFirst, pathname });

    // For external links, let the browser handle it
    if (isExternal) {
      return;
    }

    // If we're in preview mode
    if (preview?.isPreviewMode) {
      e.preventDefault();
      e.stopPropagation();

      // In edit mode, block all navigation - used for selecting sections
      if (preview.isEditMode) {
        return;
      }

      // In navigate mode, navigate within the editor
      const slug = url === '/' ? '' : url.replace(/^\//, ''); // Remove leading slash, empty string for home
      preview.navigateInEditor(slug);
      return;
    }

    // Check for hash navigation on the same page. A click is "same page" when
    // the URL has a hash AND either:
    //   - it's a relative URL whose path matches the current pathname, OR
    //   - it's absolute and shares the current origin (covers subdomains like
    //     jobs.e-voque.com which are mapped to the /join-us slug — the whole
    //     subdomain effectively IS that page, regardless of pathname).
    const [rawBaseUrl, hash] = url.split('#');
    let isSamePage = false;
    if (hash) {
      const isAbs = rawBaseUrl.startsWith('http://') || rawBaseUrl.startsWith('https://');
      if (isAbs && typeof window !== 'undefined') {
        try {
          const parsed = new URL(rawBaseUrl);
          isSamePage = parsed.origin === window.location.origin;
        } catch {
          isSamePage = false;
        }
      } else if (!isAbs) {
        isSamePage = pathname === rawBaseUrl;
      }
    }
    if (isSamePage && hash) {
      e.preventDefault();
      // If the click came from the mobile drawer, close it first so the
      // target element isn't hidden behind the overlay before/while scrolling.
      const wasMenuOpen = closeMenuFirst && isMobileMenuOpen;
      if (wasMenuOpen) {
        setIsMobileMenuOpen(false);
      }
      const scrollToTarget = () => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Push only the hash to avoid clobbering the current absolute URL
          // when the link points at a different domain that shares this origin.
          window.history.pushState({}, '', `#${hash}`);
        } else {
          console.warn('[Header] Target element not found for hash:', hash);
        }
      };
      // When the drawer was open, wait a frame for it to unmount so layout
      // settles before scrolling; otherwise a microtask is enough.
      if (wasMenuOpen) {
        requestAnimationFrame(() => requestAnimationFrame(scrollToTarget));
      } else {
        setTimeout(scrollToTarget, 0);
      }
      return;
    }

    // Check if we're already on the target URL (no hash)
    if (pathname === url) {
      e.preventDefault();
      return;
    }

    // Prevent default to use Next.js Link navigation
    e.preventDefault();
    e.stopPropagation();

    // Validate and normalize URL
    const normalizedUrl = url.trim();
    if (!normalizedUrl || normalizedUrl === '#') {
      console.warn('[Header] Invalid navigation URL:', normalizedUrl);
      return;
    }

    // Check if URL is absolute (starts with http:// or https://)
    // Next.js router.push() only works with relative URLs, so we need to use window.location for absolute URLs
    const isAbsoluteUrl = normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://');

    // Helper function to perform navigation with error handling
    const performNavigation = () => {
      try {
        if (isAbsoluteUrl) {
          // For absolute URLs, use window.location for full page navigation
          // This handles cross-domain navigation correctly
          window.location.href = normalizedUrl;
        } else {
          // For relative URLs, use Next.js router for client-side navigation
          // router.push() returns void, not a Promise, so we can't use .catch()
          // If navigation fails, it will be handled by Next.js error boundaries
          router.push(normalizedUrl);
        }
      } catch (error) {
        // Final fallback: use window.location if everything else fails
        console.error('[Header] Navigation error, using window.location fallback:', error);
        window.location.href = normalizedUrl;
      }
    };

    // On mobile, close menu first and wait for it to close before navigating
    // This prevents the page from appearing frozen
    if (closeMenuFirst && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      // Use requestAnimationFrame to ensure menu closes before navigation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Use startTransition to make navigation non-blocking
          startTransition(() => {
            // Non-blocking preload: fire-and-forget to avoid delaying navigation
            const targetSlug = normalizedUrl === '/' ? '' : normalizedUrl.replace(/^\//, '').split('?')[0];
            // Only preload for relative URLs
            if (!isAbsoluteUrl) {
              preloadPageMedia(targetSlug).catch(() => {
                // Silently handle errors - navigation continues regardless
              });
            }

            performNavigation();
          });
        });
      });
      return;
    }

    // Non-blocking preload: fire-and-forget to avoid delaying navigation
    // Media preloading happens in background and doesn't block navigation
    const targetSlug = normalizedUrl === '/' ? '' : normalizedUrl.replace(/^\//, '').split('?')[0];
    // Only preload for relative URLs
    if (!isAbsoluteUrl) {
      preloadPageMedia(targetSlug).catch(() => {
        // Silently handle errors - navigation continues regardless
      });
    }

    // Use startTransition to make navigation non-blocking
    startTransition(() => {
      performNavigation();
    });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setIsUserModalOpen(false);
      // Force a hard refresh to clear all cached state and ensure cookies are cleared
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, try to navigate away
      setIsUserModalOpen(false);
      window.location.href = '/';
    }
  };

  // Always render header, but control visibility based on scroll behavior

  // State for responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Update responsive state on mount and resize
  // In preview mode, use container width; otherwise use window width
  useEffect(() => {
    // Cache measurements to avoid repeated reads
    let cachedWidth: number | null = null;
    let resizeRafId: number | null = null;

    const updateResponsive = () => {
      // Use requestAnimationFrame to defer layout reads and avoid forced reflows
      if (resizeRafId) {
        cancelAnimationFrame(resizeRafId);
      }
      resizeRafId = requestAnimationFrame(() => {
        let width: number;

        if (preview?.isPreviewMode && headerRef.current) {
          // In preview mode, use container width - batch with other reads
          // Use getBoundingClientRect instead of offsetWidth for better performance
          const rect = headerRef.current.getBoundingClientRect();
          width = rect.width;
        } else {
          // Normal mode, use window width (no layout read needed)
          width = window.innerWidth;
        }

        // Only update state if width actually changed
        if (cachedWidth !== width) {
          cachedWidth = width;
          setIsMobile(width < 768);
          setIsTablet(width >= 768 && width < 1024);
        }
      });
    };

    updateResponsive();

    // Use ResizeObserver for window resize in normal mode (more efficient than resize event)
    let windowResizeObserver: ResizeObserver | null = null;
    if (!preview?.isPreviewMode && typeof window !== 'undefined') {
      // Create a hidden element to observe window size changes
      const resizeTarget = document.body;
      windowResizeObserver = new ResizeObserver(() => {
        updateResponsive();
      });
      windowResizeObserver.observe(resizeTarget);
    }

    if (preview?.isPreviewMode && headerRef.current) {
      // In preview mode, observe container size changes
      const resizeObserver = new ResizeObserver(() => {
        updateResponsive();
      });
      resizeObserver.observe(headerRef.current);

      return () => {
        resizeObserver.disconnect();
        if (resizeRafId) {
          cancelAnimationFrame(resizeRafId);
        }
        if (windowResizeObserver) {
          windowResizeObserver.disconnect();
        }
      };
    }

    // Cleanup for window resize observer in normal mode
    return () => {
      if (resizeRafId) {
        cancelAnimationFrame(resizeRafId);
      }
      if (windowResizeObserver) {
        windowResizeObserver.disconnect();
      }
    };
  }, [preview?.isPreviewMode]);

  // Calculate left offset for admin routes
  const getLeftOffset = () => {
    if (!isAdminRoute || !adminSidebarContext) return '0';

    // On mobile and tablet (< 1024px), sidebar overlays, so header should be full width
    if (isMobile || isTablet) {
      return '0';
    }

    // On desktop (>= 1024px), account for sidebar width
    return isAdminSidebarCollapsed ? '4rem' : '14rem'; // 64px collapsed, 224px expanded
  };

  const getWidth = () => {
    if (!isAdminRoute || !adminSidebarContext) return '100%';

    // On mobile and tablet (< 1024px), sidebar overlays, so header should be full width
    if (isMobile || isTablet) {
      return '100%';
    }

    // On desktop (>= 1024px), account for sidebar width
    return isAdminSidebarCollapsed
      ? 'calc(100% - 4rem)' // 100% - 64px (collapsed sidebar)
      : 'calc(100% - 14rem)'; // 100% - 224px (expanded sidebar)
  };

  // Helper function to check if a menu item is active based on current pathname
  // Use clientPathname after mount to fix SSR/hydration mismatch
  const isMenuItemActive = useCallback((itemUrl: string): boolean => {
    // Normalize URLs: remove query strings and trailing slashes
    const normalizeUrl = (url: string) => {
      if (!url) return '/';
      const normalized = url.split('?')[0].replace(/\/$/, '');
      // If empty after removing trailing slash, it's the home page
      return normalized === '' ? '/' : normalized;
    };

    // After mount, prefer clientPathname for accuracy (fixes SSR/hydration issues)
    // Before mount (SSR), use effectivePathname, but if it's null/undefined, default to '/' for home page
    // This ensures SSR renders home link as active when pathname is unavailable
    let pathnameToUse: string;
    if (isMounted && clientPathname !== null) {
      pathnameToUse = clientPathname;
    } else {
      // During SSR, if pathname is null/undefined and this is the home link, assume we're on home
      // This prevents SSR from rendering home link with transparent background
      if ((!effectivePathname || effectivePathname === '') && itemUrl === '/') {
        pathnameToUse = '/';
      } else {
        pathnameToUse = effectivePathname || '/';
      }
    }

    const normalizedPathname = normalizeUrl(pathnameToUse);
    const normalizedItemUrl = normalizeUrl(itemUrl);

    // Explicitly handle home page first: if we're on home page and this is the home link, it's active
    // This ensures the home link is always active when slug is '/'
    const isHomeMatch = (normalizedPathname === '/' || normalizedPathname === '') &&
      (normalizedItemUrl === '/' || normalizedItemUrl === '');

    // Exact match for all other paths
    const isExactMatch = normalizedPathname === normalizedItemUrl;

    const result = isHomeMatch || isExactMatch;
    return result;
  }, [effectivePathname, isMounted, clientPathname]);

  // Inject CSS for active nav link styling
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const styleId = 'nav-link-active-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      .nav-link-active {
        background-color: #003399 !important;
        color: #FFFFFF !important;
      }
      .nav-link-active:hover {
        background-color: #003399 !important;
        color: #FFFFFF !important;
        opacity: 1 !important;
      }
    `;

    return () => {
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, []);

  // Ensure mobile-only elements are hidden on desktop
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const styleId = 'header-mobile-elements-hide';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      /* Hide mobile Apply Now button and menu icon on desktop (>= 1024px) */
      @media (min-width: 1024px) {
        header button[aria-label="Toggle menu"] {
          display: none !important;
        }
        header div.lg\\:hidden.flex.items-center.order-first {
          display: none !important;
        }
      }
    `;

    return () => {
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, []);

  // Fix SSR/hydration mismatch: Use client-side pathname after mount
  // During SSR, usePathname() might return null or incorrect value, causing wrong active state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Get pathname from window.location to ensure accuracy after hydration
      const currentPath = window.location.pathname;
      setClientPathname(currentPath);

      // Force update active nav link styles immediately after mount to fix SSR/hydration mismatch
      // This ensures the home link has the correct blue background even if SSR rendered with transparent
      requestAnimationFrame(() => {
        const navLinks = document.querySelectorAll('nav a[href]');
        navLinks.forEach((link) => {
          const href = link.getAttribute('href');
          if (!href) return;

          // Normalize URLs for comparison
          const normalizeUrl = (url: string) => {
            if (!url) return '/';
            const normalized = url.split('?')[0].replace(/\/$/, '');
            return normalized === '' ? '/' : normalized;
          };

          const normalizedHref = normalizeUrl(href);
          const normalizedCurrentPath = normalizeUrl(currentPath);

          const isActive = (normalizedCurrentPath === '/' || normalizedCurrentPath === '') &&
            (normalizedHref === '/' || normalizedHref === '') ||
            normalizedCurrentPath === normalizedHref;

          if (isActive) {
            const linkElement = link as HTMLElement;
            linkElement.style.backgroundColor = '#003399';
            linkElement.style.color = '#FFFFFF';
            linkElement.setAttribute('data-active', 'true');
            linkElement.classList.add('nav-link-active');
          } else {
            const linkElement = link as HTMLElement;
            linkElement.classList.remove('nav-link-active');
          }
        });
      });
    }
  }, [pathname, effectivePathname]);

  // Update client pathname when pathname changes
  useEffect(() => {
    if (typeof window !== 'undefined' && pathname) {
      setClientPathname(pathname);
    }
  }, [pathname]);

  // Inject CSS for container queries when in preview mode
  // This makes responsive classes respond to preview container width instead of browser viewport
  useEffect(() => {
    if (!preview?.isPreviewMode || typeof window === 'undefined') return;

    const styleId = 'header-preview-container-queries';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Inject CSS that uses container queries for responsive behavior in preview mode
    styleElement.textContent = `
      /* Enable container queries on header when in preview mode */
      header[data-preview-mode="true"] {
        container-type: inline-size;
        container-name: header-preview;
      }
      
      /* ===== MOBILE: Container width < 640px ===== */
      @container header-preview (max-width: 639px) {
        /* Hide desktop elements */
        header[data-preview-mode="true"] .lg\\:hidden,
        header[data-preview-mode="true"] .md\\:hidden,
        header[data-preview-mode="true"] .sm\\:hidden { display: block !important; }
        
        header[data-preview-mode="true"] .lg\\:flex,
        header[data-preview-mode="true"] .md\\:flex,
        header[data-preview-mode="true"] .sm\\:flex { display: none !important; }
        
        header[data-preview-mode="true"] .hidden.lg\\:flex,
        header[data-preview-mode="true"] .hidden.md\\:flex,
        header[data-preview-mode="true"] .hidden.sm\\:flex { display: none !important; }
        
        /* Show mobile menu button */
        header[data-preview-mode="true"] button[aria-label="Toggle menu"] { display: flex !important; }
        
        /* Hide desktop navigation */
        header[data-preview-mode="true"] nav[class*="lg\\:flex"] { display: none !important; }
        header[data-preview-mode="true"] div[class*="lg\\:flex"] { display: none !important; }
        
        /* Show mobile logo */
        header[data-preview-mode="true"] div[class*="lg\\:hidden"] { display: block !important; }
      }
      
      /* ===== TABLET: Container width 640px - 1023px ===== */
      @container header-preview (min-width: 640px) and (max-width: 1023px) {
        /* Hide desktop elements */
        header[data-preview-mode="true"] .lg\\:hidden { display: block !important; }
        header[data-preview-mode="true"] .lg\\:flex { display: none !important; }
        header[data-preview-mode="true"] .hidden.lg\\:flex { display: none !important; }
        
        /* Show mobile menu button on tablet too */
        header[data-preview-mode="true"] button[aria-label="Toggle menu"] { display: flex !important; }
        
        /* Hide desktop navigation */
        header[data-preview-mode="true"] nav[class*="lg\\:flex"] { display: none !important; }
        header[data-preview-mode="true"] div[class*="lg\\:flex"] { display: none !important; }
        
        /* Show mobile logo */
        header[data-preview-mode="true"] div[class*="lg\\:hidden"] { display: block !important; }
      }
      
      /* ===== DESKTOP: Container width >= 1024px ===== */
      @container header-preview (min-width: 1024px) {
        /* Show desktop elements */
        header[data-preview-mode="true"] .lg\\:hidden { display: none !important; }
        header[data-preview-mode="true"] .lg\\:flex { display: flex !important; }
        header[data-preview-mode="true"] .hidden.lg\\:flex { display: flex !important; }
        
        /* Hide mobile menu button */
        header[data-preview-mode="true"] button[aria-label="Toggle menu"] { display: none !important; }
        
        /* Hide mobile Apply Now button */
        header[data-preview-mode="true"] div.lg\\:hidden.flex.items-center.order-first { display: none !important; }
        
        /* Show desktop navigation */
        header[data-preview-mode="true"] nav[class*="lg\\:flex"] { display: flex !important; }
        header[data-preview-mode="true"] div[class*="lg\\:flex"] { display: flex !important; }
        
        /* Hide mobile logo */
        header[data-preview-mode="true"] div[class*="lg\\:hidden"] { display: none !important; }
      }
    `;

    return () => {
      if (typeof window !== 'undefined') {
        const element = document.getElementById(styleId);
        if (element) {
          element.remove();
        }
      }
    };
  }, [preview?.isPreviewMode]);

  return (
    <>
      {/* Mobile & Tablet: Always visible logo in top left - Hidden when sidebar is open */}
      {hasLogo && (
        <div
          className={`${preview?.isPreviewMode ? 'absolute' : 'fixed'} top-0 left-0 p-3 sm:p-4 md:p-5 lg:hidden transition-opacity duration-300 ${isMobileMenuOpen || !showLogo ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
          style={{
            zIndex: headerZIndices.mobileLogo,
          }}
        >
          <Link href={getMenuUrl('/', false)} prefetch={true} className="flex items-center" onClick={(e) => handleNavigation(e, getMenuUrl('/', false), false)} onMouseEnter={() => handleLinkHover('/')}>
            <div
              className="flex items-center justify-center h-8"
              style={{
                backgroundColor: getBrandColor('white'),
                borderRadius: '14px',
                border: `1px solid ${getBrandColor('deepNavy')}20`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '0 10px',
              }}
            >
              <div className="relative h-6" style={{ width: '90px' }}>
                <img
                  src={logoUrl}
                  alt={logoAlt}
                  width={90}
                  height={24}
                  className="w-full h-full object-contain"
                  loading={logoLoading}
                  fetchPriority={isInitialLoad ? "high" : "auto"}
                  decoding="async"
                />
              </div>
            </div>
          </Link>
        </div>
      )}

      <header
        ref={headerRef}
        className={preview?.isPreviewMode ? "absolute top-0 translate-y-0" : "fixed top-0 translate-y-0"}
        data-preview-mode={preview?.isPreviewMode ? "true" : undefined}
        style={{
          backgroundColor: '#FFFFFF00',
          zIndex: headerZIndices.base,
          left: preview?.isPreviewMode ? '0' : getLeftOffset(),
          width: preview?.isPreviewMode ? '100%' : getWidth(),
          right: '0',
          pointerEvents: 'auto'
        }}
      >
        <div className="container mx-auto px-4 py-3 sm:py-4">
          {/* Main header row */}
          <div className="flex items-center justify-between">
            {/* Left: Logo - Hidden on mobile (shown separately above) */}
            <div className="hidden lg:flex items-center flex-shrink-0">
              {hasLogo && (
                <div
                  className="flex items-center justify-center h-8 transition-opacity duration-300"
                  style={{
                    backgroundColor: getBrandColor('white'),
                    borderRadius: '14px',
                    border: `1px solid ${getBrandColor('deepNavy')}20`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '8px 16px',
                    opacity: showLogo ? 1 : 0,
                    pointerEvents: showLogo ? 'auto' : 'none',
                    height: '56px',
                  }}
                >
                  <Link href={getMenuUrl('/', false)} prefetch={true} className="flex items-center" onClick={(e) => handleNavigation(e, getMenuUrl('/', false), false)} onMouseEnter={() => handleLinkHover('/')}>
                    <div className="relative h-12" style={{ width: '160px' }}>
                      <img
                        src={logoUrl}
                        alt={logoAlt}
                        width={160}
                        height={48}
                        className="w-full h-full object-contain"
                        loading={logoLoading}
                        fetchPriority={isInitialLoad ? "high" : "auto"}
                        decoding="async"
                        sizes="160px"
                        srcSet={logoUrl ? generateImageSrcSet(logoUrl, [160, 320]) : undefined}
                      />
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile: Empty spacer to push menu button to right */}
            <div className="lg:hidden flex-1"></div>

            {/* Center: Desktop Navigation - Centered with ghost container */}
            <div className="hidden lg:flex flex-1 justify-center items-center">
              <nav
                className="flex items-center space-x-4 xl:space-x-6 w-auto"
                style={{
                  backgroundColor: isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.15)' : getBrandColor('white'),
                  backdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                  borderRadius: '14px',
                  border: isOnFirstThreeSections ? `1px solid rgba(255, 255, 255, 0.2)` : `1px solid ${getBrandColor('deepNavy')}20`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  padding: '8px 20px',
                  height: '56px',
                }}
              >
                {activeMenuItems
                  .filter(item => item.label !== 'Join Us' && item.label !== 'Sign In' && item.label !== 'Apply Now')
                  .map((item) => {
                    const isActive = isMenuItemActive(item.url);
                    const backgroundColor = isActive ? '#003399' : 'transparent';

                    const menuUrl = getMenuUrl(item.url, item.isExternal);

                    return (
                      <Link
                        key={item.id}
                        href={menuUrl}
                        prefetch={!item.isExternal}
                        target={item.isExternal ? '_blank' : undefined}
                        rel={item.isExternal ? 'noopener noreferrer' : undefined}
                        data-active={isActive ? 'true' : 'false'}
                        className={`transition-colors whitespace-nowrap text-sm xl:text-[15px] cursor-pointer rounded-md ${isActive ? 'nav-link-active' : ''}`}
                        style={{
                          color: isActive ? '#FFFFFF' : (isOnFirstThreeSections ? '#FFFFFF' : '#000000'),
                          backgroundColor: backgroundColor,
                          textDecoration: 'none',
                          pointerEvents: 'auto',
                          padding: '8px 16px',
                          display: 'inline-block',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale'
                        }}
                        onClick={(e) => handleNavigation(e, menuUrl, item.isExternal)}
                        onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                          // Prefetch media for target page on hover
                          if (!item.isExternal) {
                            handleLinkHover(item.url);
                          }
                          if (!isActive) {
                            e.currentTarget.style.color = getBrandColor('primaryBlue');
                            e.currentTarget.style.opacity = '0.8';
                          }
                        }}
                        onFocus={() => {
                          // Also prefetch on focus for keyboard navigation
                          if (!item.isExternal) {
                            handleLinkHover(item.url);
                          }
                        }}
                        onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                          if (!isActive) {
                            e.currentTarget.style.color = isActive
                              ? getBrandColor('white')
                              : (isOnFirstThreeSections ? getBrandColor('white') : getBrandColor('black'));
                            e.currentTarget.style.opacity = '1';
                          }
                        }}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
              </nav>
            </div>

            {/* Right: Desktop CTA Buttons or Mobile Menu Button */}
            <div className="flex items-center flex-shrink-0 lg:ml-0 ml-auto gap-2" style={{ zIndex: headerZIndices.base + 1 }}>
              {/* Desktop CTA Buttons or User Button */}
              <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
                {user ? (
                  // User is logged in - show user button
                  <button
                    onClick={() => setIsUserModalOpen(true)}
                    className="flex items-center gap-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.15)' : getBrandColor('white'),
                      backdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                      WebkitBackdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                      border: isOnFirstThreeSections ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${getBrandColor('deepNavy')}20`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      padding: '8px 16px',
                      height: '56px',
                    }}
                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.backgroundColor = isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.25)' : getBrandColor('primaryBlue') + '10';
                    }}
                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.backgroundColor = isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.15)' : getBrandColor('white');
                    }}
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user?.profileImage ?? undefined} alt={user?.firstName || 'User'} />
                      <AvatarFallback
                        className="text-xs"
                        style={{
                          backgroundColor: getBrandColor('primaryBlue'),
                          color: getBrandColor('white'),
                        }}
                      >
                        {user?.firstName?.[0] || ''}{user?.lastName?.[0] || ''}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                ) : (
                  // User is not logged in - show Apply Now, Join Us and Sign In buttons
                  activeMenuItems
                    .filter(item => item.label === 'Apply Now' || item.label === 'Join Us' || item.label === 'Sign In')
                    .map((item) => {
                      const isSignIn = item.label === 'Sign In';
                      const isJoinUs = item.label === 'Join Us';
                      const isApplyNow = item.label === 'Apply Now';
                      // Apply Now always targets the join-us form anchor so it
                      // scrolls on /join-us and deep-links to the form elsewhere.
                      const baseUrl = getMenuUrl(item.url, item.isExternal);
                      const effectiveUrl = isApplyNow ? 'https://jobs.e-voque.com#join-us-form' : baseUrl;
                      return (
                        <Button
                          key={item.id}
                          asChild
                          variant={(isJoinUs || isApplyNow) ? 'default' : 'outline'}
                          size="sm"
                          className="whitespace-nowrap"
                          style={isSignIn ? {
                            border: 'none',
                            borderColor: isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.3)' : getBrandColor('deepNavy'),
                            color: isOnFirstThreeSections ? getBrandColor('white') : getBrandColor('deepNavy'),
                            backgroundColor: isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.15)' : getBrandColor('white'),
                            backdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                            WebkitBackdropFilter: isOnFirstThreeSections ? 'blur(10px)' : 'none',
                            height: '56px',
                            padding: '8px 16px',
                          } : (isJoinUs || isApplyNow) ? {
                            backgroundColor:
                              currentSection === 'hero' ? getBrandColor('primaryBlue') :
                                currentSection === 'about' ? '#4A8A3A' : // Darker green for better contrast (WCAG AA compliant: 4.5:1 with white)
                                  currentSection === 'third' ? getBrandColor('accentOrange') :
                                    getBrandColor('primaryBlue'),
                            color: getBrandColor('white'),
                            border: 'none',
                            height: '56px',
                            padding: '8px 16px',
                          } : undefined}
                          onMouseEnter={isSignIn ? (e: MouseEvent<HTMLButtonElement>) => {
                            e.currentTarget.style.backgroundColor = isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.25)' : getBrandColor('primaryBlue') + 'CC';
                            e.currentTarget.style.color = getBrandColor('white');
                          } : (isJoinUs || isApplyNow) ? (e: MouseEvent<HTMLButtonElement>) => {
                            e.currentTarget.style.opacity = '0.9';
                          } : undefined}
                          onMouseLeave={isSignIn ? (e: MouseEvent<HTMLButtonElement>) => {
                            e.currentTarget.style.backgroundColor = isOnFirstThreeSections ? 'rgba(255, 255, 255, 0.15)' : getBrandColor('white');
                            e.currentTarget.style.color = isOnFirstThreeSections ? getBrandColor('white') : getBrandColor('deepNavy');
                          } : (isJoinUs || isApplyNow) ? (e: MouseEvent<HTMLButtonElement>) => {
                            e.currentTarget.style.opacity = '1';
                          } : undefined}
                        >
                          <Link
                            href={effectiveUrl}
                            prefetch={!item.isExternal}
                            target={item.isExternal ? '_blank' : undefined}
                            rel={item.isExternal ? 'noopener noreferrer' : undefined}
                            onClick={(e) => {
                              handleNavigation(e, effectiveUrl, item.isExternal);
                            }}
                            onMouseEnter={() => !item.isExternal && handleLinkHover(item.url)}
                          >
                            {item.label}
                          </Link>
                        </Button>
                      );
                    })
                )}
              </div>

              {/* Mobile & Tablet: Apply Now Button (left of menu icon) - Always first */}
              {!user && (
                <div className="lg:hidden flex items-center order-first">
                  {(() => {
                    const applyNowItem = activeMenuItems.find(item => item.label === 'Apply Now');
                    if (!applyNowItem) return null;

                    // Always target the join-us form anchor so the mobile button
                    // matches the desktop scroll-to-form behavior.
                    const applyNowUrl = 'https://jobs.e-voque.com#join-us-form';

                    return (
                      <Button
                        asChild
                        size="sm"
                        className="whitespace-nowrap"
                        style={{
                          backgroundColor: getBrandColor('primaryBlue'),
                          color: getBrandColor('white'),
                          border: 'none',
                          borderRadius: '14px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                        onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        <Link
                          href={applyNowUrl}
                          prefetch={!applyNowItem.isExternal}
                          target={applyNowItem.isExternal ? '_blank' : undefined}
                          rel={applyNowItem.isExternal ? 'noopener noreferrer' : undefined}
                          onClick={(e) => {
                            handleNavigation(e, applyNowUrl, applyNowItem.isExternal);
                          }}
                          onMouseEnter={() => !applyNowItem.isExternal && handleLinkHover(applyNowItem.url)}
                        >
                          {applyNowItem.label}
                        </Link>
                      </Button>
                    );
                  })()}
                </div>
              )}

              {/* Mobile & Tablet Menu Button - Always last */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden flex items-center justify-center h-8 w-8 rounded-md transition-colors relative order-last"
                style={{
                  color: getBrandColor('white'),
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '14px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: headerZIndices.mobileLogo + 1,
                  position: 'relative',
                }}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  Icons?.X ? <Icons.X className="w-4 h-4" /> : <XIconPlaceholder className="w-4 h-4" />
                ) : (
                  Icons?.Menu ? <Icons.Menu className="w-4 h-4" /> : <MenuIconPlaceholder className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile & Tablet Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{ height: '100vh', zIndex: headerZIndices.overlay }}
          />
        )}

        {/* Mobile & Tablet Sidebar Menu */}
        {isMobileMenuOpen && (
          <div
            className="fixed top-0 left-0 right-0 h-full w-full bg-white shadow-2xl transform transition-transform duration-300 ease-in-out lg:hidden translate-x-0"
            style={{
              backgroundColor: getBrandColor('white'),
              zIndex: headerZIndices.drawer,
              height: '100vh',
            }}
          >
            <div className="flex flex-col h-full">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 sm:p-5 md:p-6 border-b" style={{ borderColor: getBrandColor('deepNavy') + '20', paddingBottom: '12px' }}>
                {hasLogo && (
                  <Link href="/" prefetch={true} className="flex items-center" onClick={(e) => {
                    handleNavigation(e, '/', false, true);
                  }} onMouseEnter={() => handleLinkHover('/')}>
                    <div className="relative h-10 sm:h-11 md:h-12" style={{ width: '140px', maxWidth: '160px' }}>
                      <img
                        src={logoUrl}
                        alt={logoAlt}
                        width={160}
                        height={48}
                        className="w-full h-full object-contain"
                        loading={logoLoading}
                        fetchPriority={isInitialLoad ? "high" : "auto"}
                        decoding="async"
                      />
                    </div>
                  </Link>
                )}
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 sm:p-2.5 md:p-3 rounded-md transition-colors"
                  style={{ color: getBrandColor('deepNavy') }}
                  aria-label="Close menu"
                >
                  {Icons?.X ? <Icons.X className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" /> : <XIconPlaceholder className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />}
                </button>
              </div>

              {/* Sidebar Content - Scrollable */}
              <div
                className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 pb-40"
                style={{ paddingTop: '12px', paddingBottom: '40px' }}
              >
                {/* Mobile & Tablet Navigation - Only links (no buttons) */}
                <nav className="flex flex-col space-y-2 sm:space-y-3">
                  {activeMenuItems
                    .filter(item => item.label !== 'Join Us' && item.label !== 'Sign In' && item.label !== 'Apply Now')
                    .map((item) => {
                      const isActive = isMenuItemActive(item.url);
                      const menuUrl = getMenuUrl(item.url, item.isExternal);
                      return (
                        <Link
                          key={item.id}
                          href={menuUrl}
                          target={item.isExternal ? '_blank' : undefined}
                          rel={item.isExternal ? 'noopener noreferrer' : undefined}
                          className="transition-colors py-4 sm:py-5 md:py-6 px-4 sm:px-5 md:px-6 rounded-md text-lg sm:text-xl md:text-2xl font-medium cursor-pointer"
                          style={{
                            color: isActive ? getBrandColor('white') : getBrandColor('deepNavy'),
                            backgroundColor: isActive ? getBrandColor('primaryBlue') : 'transparent',
                            pointerEvents: 'auto'
                          }}
                          onClick={(e) => {
                            handleNavigation(e, menuUrl, item.isExternal, true);
                          }}
                          onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                            if (!isActive) {
                              e.currentTarget.style.color = getBrandColor('white');
                              e.currentTarget.style.backgroundColor = getBrandColor('deepNavy');
                            }
                          }}
                          onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                            if (!isActive) {
                              e.currentTarget.style.color = getBrandColor('deepNavy');
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                </nav>

                {(subscriptionForm || (footerMenuSection && (footerMenuSection.content?.socialLinks as Array<{
                  platform: string;
                  url: string;
                  isActive?: boolean;
                }>)?.length > 0)) && (
                    <div className="mt-8 pt-8 border-t" style={{ borderColor: getBrandColor('deepNavy') + '20' }}>
                      {subscriptionForm && (
                        <>
                          <h3 className="text-lg font-bold mb-4" style={{ color: getBrandColor('deepNavy') }}>
                            Subscribe to our Newsletter
                          </h3>
                          <FormRenderer
                            form={{
                              id: subscriptionForm.id,
                              name: subscriptionForm.name,
                              slug: subscriptionForm.slug,
                              description: subscriptionForm.description || null,
                              fields: subscriptionForm.fields.map((f) => ({
                                id: f.id,
                                name: f.name,
                                label: f.label,
                                type: f.type,
                                placeholder: f.placeholder,
                                required: f.required,
                                options: f.options,
                                validation: f.validation,
                                order: f.order,
                              })),
                              settings: subscriptionForm.settings || undefined,
                            }}
                            className="space-y-3"
                          />
                        </>
                      )}

                      {footerMenuSection && (footerMenuSection.content?.socialLinks as Array<{
                        platform: string;
                        url: string;
                        isActive?: boolean;
                      }>)?.length > 0 && (
                          <div className={subscriptionForm ? 'mt-8 pt-6 border-t' : ''} style={subscriptionForm ? { borderColor: getBrandColor('deepNavy') + '20' } : undefined}>
                            <h3 className="text-lg font-bold mb-4" style={{ color: getBrandColor('deepNavy') }}>
                              Follow Us
                            </h3>
                            <div className="flex items-center gap-4">
                              {(footerMenuSection.content?.socialLinks as Array<{
                                platform: string;
                                url: string;
                                isActive?: boolean;
                              }>)
                                .filter(link => link.isActive !== false)
                                .map((link, index) => {
                                  const isInstagram = link.platform.toLowerCase() === 'instagram';
                                  const isTikTok = link.platform.toLowerCase() === 'tiktok';

                                  return (
                                    <a
                                      key={index}
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 hover:scale-110"
                                      style={{
                                        backgroundColor: getBrandColor('primaryBlue') + '15',
                                        color: getBrandColor('primaryBlue'),
                                        border: `1px solid ${getBrandColor('primaryBlue')}30`,
                                      }}
                                      onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                                        e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue');
                                        e.currentTarget.style.color = getBrandColor('white');
                                        e.currentTarget.style.borderColor = getBrandColor('primaryBlue');
                                      }}
                                      onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                                        e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue') + '15';
                                        e.currentTarget.style.color = getBrandColor('primaryBlue');
                                        e.currentTarget.style.borderColor = getBrandColor('primaryBlue') + '30';
                                      }}
                                      aria-label={`Visit our ${link.platform}`}
                                    >
                                      {isInstagram ? (
                                        Icons?.Instagram ? <Icons.Instagram className="w-6 h-6" /> : <span className="w-6 h-6" />
                                      ) : isTikTok ? (
                                        <TikTokIcon className="w-6 h-6" size={24} />
                                      ) : (
                                        Icons?.ExternalLink ? <Icons.ExternalLink className="w-6 h-6" /> : <span className="w-6 h-6" />
                                      )}
                                    </a>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
              </div>

              {/* Fixed Bottom Section - Buttons or User Button */}
              <div className="p-4 sm:p-5 md:p-6 border-t bg-white" style={{ borderColor: getBrandColor('deepNavy') + '20' }}>
                {user ? (
                  // User is logged in - show user button
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsUserModalOpen(true);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-md transition-colors hover:bg-gray-100"
                    style={{
                      color: getBrandColor('deepNavy'),
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.profileImage ?? undefined} alt={`${user?.firstName || ''} ${user?.lastName || ''}`} />
                      <AvatarFallback
                        className="bg-blue-600 text-white"
                      >
                        {user?.firstName?.[0] || ''}{user?.lastName?.[0] || ''}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{user?.firstName || ''} {user?.lastName || ''}</p>
                      <p className="text-sm opacity-70">{user?.email || ''}</p>
                    </div>
                  </button>
                ) : (
                  // User is not logged in - show Apply Now, Join Us and Sign In buttons
                  <nav
                    className="flex flex-col space-y-2 sm:space-y-3"
                    style={{ marginBottom: '40px' }}
                  >
                    {activeMenuItems
                      .filter(item => item.label === 'Apply Now' || item.label === 'Join Us' || item.label === 'Sign In')
                      .map((item) => {
                        const isJoinUs = item.label === 'Join Us';
                        const isSignIn = item.label === 'Sign In';
                        const isApplyNow = item.label === 'Apply Now';
                        const baseUrl = getMenuUrl(item.url, item.isExternal);
                        const effectiveUrl = isApplyNow ? 'https://jobs.e-voque.com#join-us-form' : baseUrl;
                        return (
                          <Button
                            key={item.id}
                            asChild
                            variant={(isJoinUs || isApplyNow) ? 'default' : 'outline'}
                            size="lg"
                            className="w-full py-4 sm:py-5 md:py-6 text-lg sm:text-xl md:text-2xl font-medium"
                            style={(isJoinUs || isApplyNow) ? {
                              backgroundColor:
                                currentSection === 'hero' ? getBrandColor('primaryBlue') :
                                  currentSection === 'about' ? '#4A8A3A' : // Darker green for better contrast (WCAG AA compliant: 4.5:1 with white)
                                    currentSection === 'third' ? getBrandColor('accentOrange') :
                                      getBrandColor('primaryBlue'),
                              color: getBrandColor('white'),
                              border: 'none',
                            } : isSignIn ? {
                              border: 'none',
                            } : undefined}
                            onMouseEnter={isSignIn ? (e: MouseEvent<HTMLButtonElement>) => {
                              e.currentTarget.style.backgroundColor = getBrandColor('primaryBlue');
                              e.currentTarget.style.color = getBrandColor('white');
                            } : undefined}
                            onMouseLeave={isSignIn ? (e: MouseEvent<HTMLButtonElement>) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '';
                            } : undefined}
                          >
                            <Link
                              href={effectiveUrl}
                              target={item.isExternal ? '_blank' : undefined}
                              rel={item.isExternal ? 'noopener noreferrer' : undefined}
                              onClick={(e) => {
                                handleNavigation(e, effectiveUrl, item.isExternal, true);
                              }}
                            >
                              {item.label}
                            </Link>
                          </Button>
                        );
                      })}
                  </nav>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Modal */}
        <Dialog open={isUserModalOpen} onOpenChange={setIsUserModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>User Profile</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center gap-4 mb-6">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.profileImage ?? undefined} alt={`${user?.firstName || ''} ${user?.lastName || ''}`} />
                  <AvatarFallback className="bg-blue-600 text-white">
                    {user?.firstName?.[0] || ''}{user?.lastName?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-lg">{user?.firstName || ''} {user?.lastName || ''}</p>
                  <p className="text-sm text-gray-600">{user?.email || ''}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  asChild
                >
                  <Link href={getMenuUrl('/', false)} prefetch={true} onClick={() => setIsUserModalOpen(false)}>
                    {Icons?.Home ? <Icons.Home className="h-4 w-4 mr-2" /> : <span className="h-4 w-4 mr-2" />}
                    Home Page
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  asChild
                >
                  <Link href="/admin/dashboard" onClick={() => setIsUserModalOpen(false)}>
                    {Icons?.LayoutDashboard ? <Icons.LayoutDashboard className="h-4 w-4 mr-2" /> : <span className="h-4 w-4 mr-2" />}
                    Dashboard
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  {Icons?.LogOut ? <Icons.LogOut className="h-4 w-4 mr-2" /> : <span className="h-4 w-4 mr-2" />}
                  Log Out
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </header>
    </>
  );
}
